import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';

const PRINT_HUB_URL = import.meta.env.VITE_PRINT_HUB_URL;
const PRINT_API_URL = import.meta.env.VITE_PRINT_API_URL;
const PING_TIMEOUT_MS = 3000;
const GROUP_JOIN_SETTLE_MS = 500;

export type CertJobStatus =
  | 'connecting'
  | 'pinging'
  | 'monitor_offline'
  | 'creating'
  | 'waiting'
  | 'complete'
  | 'error'
  | 'skipped';

export type CertJob = {
  calibrationId: string;
  jobNumber: string;
  sequenceNumber: string;
  status: CertJobStatus;
  startedAt: Date;
  eventNum?: number;
  fileName?: string;
  pdfBase64?: string;
  pdfIncluded?: boolean;
  message?: string;
  errorMessage?: string;
};

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

type StartCertJobParams = {
  calibrationId: string;
  jobNumber: string;
  sequenceNumber: string;
  userName: string;
  environmentId: string;
  eventNum?: number;
};

type StartCertJobResult = {
  started: boolean;
  error?: string;
};

type CertificateStore = {
  pendingJob: CertJob | null;
  connectionStatus: ConnectionStatus;

  startCertJob: (params: StartCertJobParams) => Promise<StartCertJobResult>;
  skipCertJob: () => void;
  clearPdfData: () => void;
  clearJob: () => void;
  disconnect: () => Promise<void>;
};

// Module-scoped variables — outside store state to avoid serialization/reactivity issues
let connection: HubConnection | null = null;
let pingResolver: ((online: boolean) => void) | null = null;
let pingTimeout: ReturnType<typeof setTimeout> | null = null;
let currentUserName = '';
let currentEnvironmentId = '';

export const useCertificateStore = create<CertificateStore>()(
  devtools(
    (set, get) => ({
      pendingJob: null,
      connectionStatus: 'disconnected',

      startCertJob: async ({
        calibrationId,
        jobNumber,
        sequenceNumber,
        userName,
        environmentId,
        eventNum
      }) => {
        // Validate required configuration
        if (!PRINT_HUB_URL || !PRINT_API_URL) {
          console.error(
            '[CertificateStore] Certificate service URLs not configured'
          );
          set(state => ({
            pendingJob: state.pendingJob
              ? {
                  ...state.pendingJob,
                  status: 'error',
                  errorMessage: 'Certificate service URL not configured'
                }
              : {
                  calibrationId,
                  jobNumber,
                  sequenceNumber,
                  status: 'error',
                  startedAt: new Date(),
                  eventNum,
                  errorMessage: 'Certificate service URL not configured'
                },
            connectionStatus: 'error'
          }));
          return {
            started: false,
            error: 'Certificate service URL not configured'
          };
        }

        // Disconnect any existing connection
        await get().disconnect();

        // Store filtering params for SignalR event handlers
        currentUserName = userName;
        currentEnvironmentId = environmentId;

        // Create pending job
        set({
          pendingJob: {
            calibrationId,
            jobNumber,
            sequenceNumber,
            status: 'connecting',
            startedAt: new Date(),
            eventNum
          },
          connectionStatus: 'connecting'
        });

        // Build HubConnection
        const conn = new HubConnectionBuilder()
          .withUrl(PRINT_HUB_URL)
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();

        // PongPrintMonitor handler — resolves the ping promise
        conn.on('PongPrintMonitor', (pongUserName: string, envId: string) => {
          if (envId?.toLowerCase() !== currentEnvironmentId?.toLowerCase())
            return;
          if (pongUserName?.toLowerCase() !== currentUserName?.toLowerCase())
            return;

          if (pingResolver) {
            if (pingTimeout) {
              clearTimeout(pingTimeout);
              pingTimeout = null;
            }
            pingResolver(true);
            pingResolver = null;
          }
        });

        // CreatePendingJobsComplete handler — updates job to complete/error
        conn.on(
          'CreatePendingJobsComplete',
          (
            user: string,
            envId: string,
            _sessionId: string,
            message: string,
            fileName: string,
            pdfBase64: string,
            pdfIncludedStr: string,
            errorMessage: string
          ) => {
            if (envId?.toLowerCase() !== currentEnvironmentId?.toLowerCase())
              return;
            if (user?.toLowerCase() !== currentUserName?.toLowerCase()) return;

            const pdfIncluded = pdfIncludedStr?.toLowerCase() === 'true';
            const success = !errorMessage;

            set(state => ({
              pendingJob: state.pendingJob
                ? {
                    ...state.pendingJob,
                    status: success ? 'complete' : 'error',
                    fileName: fileName || undefined,
                    pdfBase64: pdfBase64 || undefined,
                    pdfIncluded,
                    message: message || undefined,
                    errorMessage: errorMessage || undefined
                  }
                : null
            }));
          }
        );

        conn.onreconnected(async () => {
          set({ connectionStatus: 'connected' });
          try {
            await conn.invoke('JoinPrintListenerGroup', currentEnvironmentId);
          } catch (err) {
            console.error(
              '[CertificateStore] Failed to rejoin listener group:',
              err
            );
          }
        });

        conn.onclose(() => {
          set({ connectionStatus: 'disconnected' });
        });

        // Start connection
        try {
          await conn.start();
          connection = conn;
          set({ connectionStatus: 'connected' });
        } catch (err) {
          console.error('[CertificateStore] Connection failed:', err);
          set(state => ({
            pendingJob: state.pendingJob
              ? {
                  ...state.pendingJob,
                  status: 'error',
                  errorMessage: 'Failed to connect to print monitor'
                }
              : null,
            connectionStatus: 'error'
          }));
          return {
            started: false,
            error: 'Failed to connect to print monitor'
          };
        }

        // Join listener group
        try {
          await conn.invoke('JoinPrintListenerGroup', currentEnvironmentId);
          await new Promise(r => setTimeout(r, GROUP_JOIN_SETTLE_MS));
        } catch (err) {
          console.error(
            '[CertificateStore] Failed to join listener group:',
            err
          );
          set(state => ({
            pendingJob: state.pendingJob
              ? {
                  ...state.pendingJob,
                  status: 'error',
                  errorMessage: 'Failed to join print listener group'
                }
              : null
          }));
          await get().disconnect();
          return {
            started: false,
            error: 'Failed to join print listener group'
          };
        }

        // Ping monitor
        set(state => ({
          pendingJob: state.pendingJob
            ? { ...state.pendingJob, status: 'pinging' }
            : null
        }));

        try {
          const response = await fetch(
            `${PRINT_API_URL}/api/print/ping/monitor/${encodeURIComponent(currentEnvironmentId)}/${encodeURIComponent(currentUserName)}`,
            { method: 'POST' }
          );

          if (!response.ok) {
            set(state => ({
              pendingJob: state.pendingJob
                ? { ...state.pendingJob, status: 'monitor_offline' }
                : null
            }));
            await get().disconnect();
            return {
              started: false,
              error: 'Print monitor is not currently online'
            };
          }
        } catch {
          set(state => ({
            pendingJob: state.pendingJob
              ? { ...state.pendingJob, status: 'monitor_offline' }
              : null
          }));
          await get().disconnect();
          return {
            started: false,
            error: 'Failed to ping print monitor'
          };
        }

        // Wait for pong (or timeout)
        const isOnline = await new Promise<boolean>(resolve => {
          pingResolver = online => resolve(online);

          pingTimeout = setTimeout(() => {
            pingResolver = null;
            pingTimeout = null;
            resolve(false);
          }, PING_TIMEOUT_MS);
        });

        if (!isOnline) {
          set(state => ({
            pendingJob: state.pendingJob
              ? { ...state.pendingJob, status: 'monitor_offline' }
              : null
          }));
          await get().disconnect();
          return {
            started: false,
            error: 'Print monitor is not currently online'
          };
        }

        // Create pending jobs
        set(state => ({
          pendingJob: state.pendingJob
            ? { ...state.pendingJob, status: 'creating' }
            : null
        }));

        try {
          const response = await fetch(
            `${PRINT_API_URL}/api/print/create/${encodeURIComponent(currentEnvironmentId)}/${encodeURIComponent(currentUserName)}`,
            { method: 'POST' }
          );

          if (!response.ok) {
            set(state => ({
              pendingJob: state.pendingJob
                ? {
                    ...state.pendingJob,
                    status: 'error',
                    errorMessage: 'Failed to create pending jobs'
                  }
                : null
            }));
            await get().disconnect();
            return {
              started: false,
              error: 'Failed to create pending jobs'
            };
          }
        } catch {
          set(state => ({
            pendingJob: state.pendingJob
              ? {
                  ...state.pendingJob,
                  status: 'error',
                  errorMessage: 'Failed to create pending jobs'
                }
              : null
          }));
          await get().disconnect();
          return {
            started: false,
            error: 'Failed to create pending jobs'
          };
        }

        // Success — waiting for SignalR CreatePendingJobsComplete
        set(state => ({
          pendingJob: state.pendingJob
            ? { ...state.pendingJob, status: 'waiting' }
            : null
        }));

        return { started: true };
      },

      skipCertJob: () => {
        set(state => ({
          pendingJob: state.pendingJob
            ? { ...state.pendingJob, status: 'skipped' }
            : null
        }));
        get().disconnect();
      },

      clearPdfData: () => {
        set(state => ({
          pendingJob: state.pendingJob
            ? { ...state.pendingJob, pdfBase64: undefined }
            : null
        }));
      },

      clearJob: () => {
        set({ pendingJob: null });
      },

      disconnect: async () => {
        if (pingTimeout) {
          clearTimeout(pingTimeout);
          pingTimeout = null;
        }
        pingResolver = null;

        const conn = connection;
        if (!conn) return;

        try {
          if (conn.state === HubConnectionState.Connected) {
            await conn.invoke('LeavePrintListenerGroup', currentEnvironmentId);
          }
        } catch (err) {
          console.error(
            '[CertificateStore] Failed to leave listener group:',
            err
          );
        }

        try {
          await conn.stop();
        } catch (err) {
          console.error('[CertificateStore] Failed to stop connection:', err);
        }

        connection = null;
        set({ connectionStatus: 'disconnected' });
      }
    }),
    { name: 'certificate-store' }
  )
);
